import { encodeFunctionData } from 'viem';
import BalanceOfAbi from '../../../src/utils/BalanceOfAbi.json';
import { ALCHEMY_API_KEY, usdt_token, RAIN_ARBI_CONTRACT_ADDRESS } from "../../utils/Enviroment";
import useWeb3 from "../../hooks/useWeb3";
import { useRecoilValue } from 'recoil';
import { userSmartAccount, userSmartAccountClient, userPublicClient } from '../../components/RecoilAtom';
import { signPreparedCalls } from '@account-kit/wallet-client';
import { useSessionPermissions } from '../checkSessionPermissions';
import { getTxHashAndReceipt, handleGasFeeWithBuffer, sendGasFeeAsUsdt, convertToMwei } from '../../utils/helpers';
import { Network, Alchemy } from 'alchemy-sdk';
import { TOKEN_USDT, USDT_MOCK_VALUE, USDT_UNIT, RAIN_UNIT } from '../../utils/constants';


export const useWithdrawalAmountHook = () => {
    const settings = {
        apiKey: ALCHEMY_API_KEY,
        network: Network.ARB_MAINNET,
    };
    const alchemy = new Alchemy(settings);
    const web3 = useWeb3();
    const smartAccount = useRecoilValue(userSmartAccount);
    const targetABI = BalanceOfAbi;
    const smartAccountClient = useRecoilValue(userSmartAccountClient);
    const publicClient = useRecoilValue(userPublicClient);
    const { grantPermissions } = useSessionPermissions()

    const calculateGasBuffer = async (tokenAddress, withdrawalAddress, withdrawalAmountInWei) => {
        const callArgs = [{
            to: tokenAddress,
            data: encodeFunctionData({
                abi: targetABI,
                functionName: "transfer",
                args: [withdrawalAddress, web3.utils.toHex(withdrawalAmountInWei)]
            })
        },
        {
            to: usdt_token,
            data: sendGasFeeAsUsdt(USDT_MOCK_VALUE)
        }]
        const gasFee = await handleGasFeeWithBuffer(smartAccount, callArgs, alchemy)
        return gasFee;
    }

    const withdrawalAmountTransaction = async (withdrawalAddress, withdrawalAmount, tokenType, tokenBalance) => {
        const isUSDT = tokenType === TOKEN_USDT;
        const tokenAddress = isUSDT ? usdt_token : RAIN_ARBI_CONTRACT_ADDRESS;
        const withdrawalAmountInWei = isUSDT ? convertToMwei(withdrawalAmount, USDT_UNIT) : convertToMwei(withdrawalAmount, RAIN_UNIT);
        const tokenBalanceInWei = isUSDT ? convertToMwei(tokenBalance, USDT_UNIT) : convertToMwei(tokenBalance, RAIN_UNIT);
        const estimatedGasFee = await calculateGasBuffer(tokenAddress, withdrawalAddress, withdrawalAmountInWei);
        const estimatedGasFeeInWei = convertToMwei((Math.floor(Number(estimatedGasFee) * 1e6) / 1e6), 'mwei');
        let latestAmountToWithdraw = withdrawalAmountInWei;

        if (tokenType) {
            const estimatedTxCost = (+withdrawalAmountInWei) + (+estimatedGasFeeInWei);
            const isSufficientBalance = (+tokenBalanceInWei) > (+estimatedTxCost);
            if (!isSufficientBalance) {
                const calculatedAmount = (+withdrawalAmountInWei) - (+estimatedGasFeeInWei);
                latestAmountToWithdraw = calculatedAmount;
            }
        }

        try {
            const { userPermissions: permissions, userSessionKey: sessionKey } = await grantPermissions()
            const callArgs = [{
                to: tokenAddress,
                data: encodeFunctionData({
                    abi: targetABI,
                    functionName: "transfer",
                    args: [withdrawalAddress, web3.utils.toHex(latestAmountToWithdraw)]
                })
            },
            {
                to: usdt_token,
                data: sendGasFeeAsUsdt(+estimatedGasFee)
            }]
            const preparedCalls = await smartAccountClient.prepareCalls({
                calls: callArgs,
                from: smartAccount,
                capabilities: { permissions },
            });
            const signedCalls = await signPreparedCalls(sessionKey, preparedCalls);
            const sendResult = await smartAccountClient.sendPreparedCalls({
                ...signedCalls,
                capabilities: { permissions },
            });
            const { preparedCallIds } = sendResult;
            const { txHash, receipt } = await getTxHashAndReceipt({
                preparedCallId: preparedCallIds[0],
                smartAccountClient,
                publicClient,
            });
            return { receipt, error: null, txHash };
        } catch (error) {
            console.error('Error in withdrawal transaction:', error);
            return { receipt: null, error, txHash: null };
        }
    };

    return { withdrawalAmountTransaction };
};